import { updateOrganizationSettings } from 'actions';
import { ICONS } from 'consts/consts';
import { isEqual } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import { useCurrentOrganization } from 'utils/util/hooks';
import { getFriendlyErrorMessageIfErrorOccurred } from 'utils/util/util';
import CardHeaderTitle from 'views/Widgets/Cards/CardHeaderTitle';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import { YYYY_MM_DD_WITH_DASHES_FORMAT } from 'views/Widgets/Inputs/DatePicker';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import PromotionPacketApproversInput from './PromotionPacketApproversInput';
import PromotionPacketQuestionsInput, {
  ArchivableQuestion,
} from './PromotionPacketQuestionsInput';
import { TemplatesAdminPageProps } from './TemplatesAdminPageBase';
import {
  ContentTemplate,
  PromotionPacketQuestionContent,
  TEMPLATE_STATUSES,
  TEMPLATE_TYPE_AVAILABILITY,
  TEMPLATE_TYPES,
} from './types';

type PromotionPacketSettings = {
  default_promotion_packet_questions: Record<string, unknown>[] | null;
  default_promotion_packet_description: string | null;
  default_promotion_effective_date: string | null;
  default_promotion_level_options: unknown[] | null;
  default_promotion_approvers: Record<string, unknown> | null;
};

const LevelOptionDeleteButton: FC<{ onDelete: () => void }> = ({
  onDelete,
}) => {
  const { formatMessage } = useIntl();
  return (
    <Button
      color="white"
      size="sm"
      onClick={onDelete}
      aria-label={formatMessage({
        id: 'app.views.administration.content_administration.templates.promo.settings.level_options.delete',
        defaultMessage: 'Delete',
      })}
    >
      <i className={ICONS.TRASH} />
    </Button>
  );
};

// Feedback giver questions are content templates. In the settings form they
// are edited as plain questions with an archived flag, and turned back into
// template mutations when the form is submitted.
const templateToQuestion = (template: ContentTemplate): ArchivableQuestion => ({
  ...((template.content as PromotionPacketQuestionContent).question ?? {}),
  label: template.name,
  archived: template.status === TEMPLATE_STATUSES.ARCHIVED,
});

const questionContent = (question: ArchivableQuestion) => {
  const { archived, ...content } = question;
  return content;
};

type RequestError = { error: unknown; hardError: unknown };

const sendRequest = (
  method: string,
  url: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> =>
  new Promise((resolve, reject) => {
    ConfirmAPI.sendRequestToConfirm(
      method,
      url,
      payload,
      (data, error, hardError) => {
        if (error || hardError || !data) {
          reject({ error, hardError } as RequestError);
          return;
        }
        resolve(data);
      }
    );
  });

const PromotionPacketQuestionsPage: FC<TemplatesAdminPageProps> = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const currentOrganization = useCurrentOrganization();

  const [templates, setTemplates] = useState<ContentTemplate[] | undefined>(
    undefined
  );
  const [loadError, setLoadError] = useState<unknown>(null);
  const [settingsObj, setSettingsObj] = useState<
    PromotionPacketSettings | undefined
  >(undefined);
  const [settingsDraft, setSettingsDraft] = useState<
    Record<string, unknown> | undefined
  >(undefined);
  const [platformLevels, setPlatformLevels] = useState<string[]>([]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'content-templates',
      {
        organization: currentOrganization.id,
        type: TEMPLATE_TYPES.PROMOTION_PACKET_QUESTION,
      },
      (data, error, hardError) => {
        if (error || hardError) {
          setLoadError(error || hardError);
          return;
        }
        setLoadError(null);
        setTemplates(data?.results ?? []);
      }
    );
    ConfirmAPI.sendRequestToConfirm(
      'GET',
      'promotion-packet-settings',
      { organization: currentOrganization.id },
      (data, error, hardError) => {
        if (error || hardError) {
          return;
        }
        setSettingsObj(data);
      }
    );
    ConfirmAPI.sendRequestToConfirm(
      'POST',
      'search/get-job-catalog-datasets',
      { organization: currentOrganization.id },
      (data, error, hardError) => {
        if (error || hardError) {
          return;
        }
        const levels = new Set<string>();
        for (const dataset of data?.datasets ?? []) {
          for (const row of dataset?.mapped_data ?? []) {
            if (row?.level) {
              levels.add(String(row.level));
            }
          }
        }
        setPlatformLevels([...levels].sort((a, b) => a.localeCompare(b)));
      }
    );
  }, [currentOrganization?.id]);

  const orderedTemplates = useMemo(
    () => [
      ...(templates ?? []).filter(
        (t) => t.status !== TEMPLATE_STATUSES.ARCHIVED
      ),
      ...(templates ?? []).filter(
        (t) => t.status === TEMPLATE_STATUSES.ARCHIVED
      ),
    ],
    [templates]
  );

  const settingsFormObject = useMemo(() => {
    if (!settingsObj) return undefined;
    return {
      organization: currentOrganization?.id,
      feedback_giver_questions: orderedTemplates.map(templateToQuestion),
      default_promotion_packet_questions:
        settingsObj.default_promotion_packet_questions ?? [],
      default_promotion_packet_description:
        settingsObj.default_promotion_packet_description ?? '',
      default_promotion_effective_date:
        settingsObj.default_promotion_effective_date ?? '',
      default_promotion_level_options: (
        settingsObj.default_promotion_level_options ?? []
      ).map((option) =>
        typeof option === 'string'
          ? { id: option, name: option, type: 'none' }
          : {
              id: (option as Record<string, unknown>)?.id ?? '',
              name: (option as Record<string, unknown>)?.name ?? '',
              type: (option as Record<string, unknown>)?.type ?? 'none',
            }
      ),
      default_promotion_approvers: settingsObj.default_promotion_approvers,
    };
  }, [settingsObj, orderedTemplates, currentOrganization?.id]);

  const normalizedLevelOptions =
    settingsFormObject?.default_promotion_level_options;

  const roleTypeObjects = useMemo(
    () => [
      {
        id: 'none',
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.role_type.not_specified',
          defaultMessage: 'Not specified',
        }),
      },
      {
        id: 'management',
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.role_type.management',
          defaultMessage: 'Management',
        }),
      },
      {
        id: 'exempt',
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.role_type.exempt',
          defaultMessage: 'Exempt',
        }),
      },
      {
        id: 'nonexempt',
        name: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.role_type.nonexempt',
          defaultMessage: 'Non-exempt',
        }),
      },
    ],
    [formatMessage]
  );

  const currentLevelOptions =
    settingsDraft?.default_promotion_level_options ??
    normalizedLevelOptions ??
    [];

  const settingsInputs = useMemo(
    () => [
      {
        name: 'default_promotion_packet_questions',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: PromotionPacketQuestionsInput,
        archiveDescription: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.packet_questions.archive_description',
          defaultMessage:
            'This question will no longer be asked when creating a promotion packet. Answers already given on existing packets are kept. Remember to save the settings.',
        }),
        autoFocus: true,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.packet_questions',
          defaultMessage: 'Packet author questions',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.packet_questions_helper',
          defaultMessage:
            'Asked to the person creating a promotion packet. Leave empty to use the default packet fields.',
        }),
      },
      {
        name: 'feedback_giver_questions',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: PromotionPacketQuestionsInput,
        archiveDescription: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.archive_description',
          defaultMessage:
            'This question will no longer be asked when giving feedback on a promotion packet. Answers already given are kept. Remember to save the settings.',
        }),
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.questions_title',
          defaultMessage: 'Questions asked to feedback givers',
        }),
        helperText: TEMPLATE_TYPE_AVAILABILITY(
          formatMessage,
          TEMPLATE_TYPES.PROMOTION_PACKET_QUESTION
        ),
      },
      {
        name: 'default_promotion_packet_description',
        type: INPUT_TYPES.RICH_TEXT_EDITOR,
        minRows: 4,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.description',
          defaultMessage: 'Packet description',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.description_helper',
          defaultMessage: 'Shown at the top of every promotion packet.',
        }),
      },
      {
        name: 'default_promotion_effective_date',
        type: INPUT_TYPES.DATE_PICKER,
        format: YYYY_MM_DD_WITH_DASHES_FORMAT,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.effective_date',
          defaultMessage: 'Default effective date',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.effective_date_helper',
          defaultMessage: 'Leave empty for no default.',
        }),
      },
      {
        name: 'default_promotion_level_options',
        type: INPUT_TYPES.TABLE_EDITOR,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.level_options',
          defaultMessage: 'Level options',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.level_options_helper',
          defaultMessage:
            'Options for the new level field on a packet. Leave empty for free text input. When every option has a role type, the packet form infers whether the new role is a management role instead of asking.',
        }),
        addText: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.level_options.add',
          defaultMessage: 'Add level',
        }),
        moreActions: LevelOptionDeleteButton,
        columns: [
          {
            name: formatMessage({
              id: 'app.views.administration.content_administration.templates.promo.settings.level_options.id',
              defaultMessage: 'Id',
            }),
            field: 'id',
            sortable: false,
            // when the platform has a job catalog, ids come from its levels
            ...(platformLevels.length > 0
              ? {
                  type: INPUT_TYPES.DROPDOWN,
                  clearable: false,
                  objects: platformLevels.map((level) => ({
                    id: level,
                    name: level,
                  })),
                }
              : { type: INPUT_TYPES.TEXT }),
          },
          {
            name: formatMessage({
              id: 'app.views.administration.content_administration.templates.promo.settings.level_options.name',
              defaultMessage: 'Name',
            }),
            field: 'name',
            sortable: false,
            type: INPUT_TYPES.TEXT,
          },
          {
            name: formatMessage({
              id: 'app.views.administration.content_administration.templates.promo.settings.level_options.role_type',
              defaultMessage: 'Role type',
            }),
            field: 'type',
            sortable: false,
            type: INPUT_TYPES.DROPDOWN,
            clearable: false,
            objects: roleTypeObjects,
          },
        ],
      },
      {
        name: 'default_promotion_approvers',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: PromotionPacketApproversInput,
        levelOptions: currentLevelOptions,
        label: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.approvers',
          defaultMessage: 'Default approvers',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.approvers_helper',
          defaultMessage:
            'New packets are routed to the approver configured for the chosen level.',
        }),
      },
    ],
    [formatMessage, currentLevelOptions, platformLevels, roleTypeObjects]
  );

  const validateSettings = useCallback(
    (obj) => {
      const errors: Record<string, string> = {};
      const ids = (obj.default_promotion_level_options ?? [])
        .map((option) => option.id?.trim() || option.name?.trim())
        .filter(Boolean);
      if (new Set(ids).size !== ids.length) {
        errors.default_promotion_level_options = formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.error.duplicate_level',
          defaultMessage: 'Each level id can only be used once.',
        });
      }
      return errors;
    },
    [formatMessage]
  );

  const transformSettingsBeforeSubmit = useCallback((obj) => {
    const levelOptions = (obj.default_promotion_level_options ?? [])
      .filter((option) => option.id?.trim() || option.name?.trim())
      .map((option) => ({
        id: option.id?.trim() || option.name?.trim(),
        name: option.name?.trim() || option.id?.trim(),
        ...(option.type && option.type !== 'none' ? { type: option.type } : {}),
      }));

    const approvers = obj.default_promotion_approvers;

    // the packet form only accepts custom author questions whose names
    // start with configs_ and silently ignores the whole set otherwise
    const packetQuestions = (obj.default_promotion_packet_questions ?? []).map(
      (question) =>
        typeof question.name === 'string' &&
        question.name.startsWith('configs_')
          ? question
          : { ...question, name: `configs_${question.name ?? ''}` }
    );

    return {
      organization: obj.organization,
      feedback_giver_questions: obj.feedback_giver_questions ?? [],
      default_promotion_packet_questions:
        packetQuestions.length > 0 ? packetQuestions : null,
      default_promotion_packet_description:
        obj.default_promotion_packet_description?.trim() || null,
      default_promotion_effective_date:
        obj.default_promotion_effective_date?.trim?.() || null,
      default_promotion_level_options:
        levelOptions.length > 0 ? levelOptions : null,
      default_promotion_approvers:
        approvers && Object.keys(approvers).length > 0 ? approvers : null,
    };
  }, []);

  const applyOrganizationSettings = useCallback(
    (data: Record<string, unknown>) => {
      const { organization_settings, ...rest } = data;
      if (organization_settings) {
        dispatch(updateOrganizationSettings(organization_settings));
      }
      return rest;
    },
    [dispatch]
  );

  // every feedback giver question is a content template: create the new
  // ones, update the edited ones, change the status of archived and
  // restored ones, then save the settings themselves
  const saveSettings = useCallback(
    async (
      obj: Record<string, unknown>,
      callback?: (data: unknown, error?: unknown, hardError?: unknown) => void
    ) => {
      const { feedback_giver_questions: questions = [], ...settings } = obj;
      const organization = currentOrganization?.id;
      const byName = new Map(
        (templates ?? []).map((t) => [templateToQuestion(t).name, t])
      );
      try {
        const savedTemplates: ContentTemplate[] = [];
        for (const question of questions as ArchivableQuestion[]) {
          const content = questionContent(question);
          const existing = question.name
            ? byName.get(question.name)
            : undefined;
          if (!existing) {
            const created = await sendRequest('POST', 'content-templates', {
              organization,
              type: TEMPLATE_TYPES.PROMOTION_PACKET_QUESTION,
              name: content.label,
              content: { question: content },
            });
            savedTemplates.push(
              applyOrganizationSettings(created) as unknown as ContentTemplate
            );
            continue;
          }
          const payload: Record<string, unknown> = {};
          const status = question.archived
            ? TEMPLATE_STATUSES.ARCHIVED
            : TEMPLATE_STATUSES.ACTIVE;
          if (existing.status !== status) {
            payload.status = status;
          }
          if (
            !isEqual(questionContent(templateToQuestion(existing)), content)
          ) {
            payload.name = content.label;
            payload.content = { question: content };
          }
          if (Object.keys(payload).length === 0) {
            savedTemplates.push(existing);
            continue;
          }
          const updated = await sendRequest(
            'PATCH',
            `content-templates/${existing.id}`,
            { organization, ...payload }
          );
          savedTemplates.push(
            applyOrganizationSettings(updated) as unknown as ContentTemplate
          );
        }
        const saved = await sendRequest(
          'PATCH',
          'promotion-packet-settings',
          settings
        );
        callback?.({
          ...applyOrganizationSettings(saved),
          templates: savedTemplates,
        });
      } catch (failure) {
        const { error, hardError } = failure as RequestError;
        callback?.(null, error, hardError);
      }
    },
    [applyOrganizationSettings, currentOrganization?.id, templates]
  );

  const settingsCallback = useCallback(
    (data, error, hardError) => {
      if (error || hardError || !data) {
        if (hardError) {
          toast.error(getFriendlyErrorMessageIfErrorOccurred(error, hardError));
        }
        return;
      }
      const { templates: savedTemplates, ...saved } = data;
      setTemplates(savedTemplates);
      setSettingsObj(saved);
      setSettingsDraft(undefined);
      toast.success(
        formatMessage({
          id: 'app.views.administration.content_administration.templates.promo.settings.toast.saved',
          defaultMessage: 'Promotion packet settings saved.',
        })
      );
    },
    [formatMessage]
  );

  if (loadError) {
    return (
      <div className="text-center text-muted py-5">
        <FormattedMessage
          id="app.views.administration.content_administration.templates.load_error"
          defaultMessage="Templates could not be loaded"
        />
      </div>
    );
  }

  if (!templates) {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.content_administration.templates.loading',
          defaultMessage: 'Loading templates...',
        })}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardHeaderTitle>
          <FormattedMessage
            id="app.views.administration.content_administration.templates.promo.settings_title"
            defaultMessage="Promotion packet settings"
          />
        </CardHeaderTitle>
      </CardHeader>
      <CardBody>
        {!settingsFormObject && <Loading />}
        {settingsFormObject && (
          <ValidatedForm
            submitPromise={saveSettings}
            object={settingsFormObject}
            inputs={settingsInputs}
            // question previews render real inputs, so a required question
            // must not block saving the settings through native validation
            noValidate
            onChange={setSettingsDraft}
            onValidate={validateSettings}
            transformObjectBeforeSubmit={transformSettingsBeforeSubmit}
            callback={settingsCallback}
            submitText={formatMessage({
              id: 'app.views.administration.content_administration.templates.promo.settings.submit',
              defaultMessage: 'Save settings',
            })}
            buttonIsBlock={false}
            buttonClassName="ms-auto w-25"
          />
        )}
      </CardBody>
    </Card>
  );
};

export default React.memo(PromotionPacketQuestionsPage);
